import { Socket, io } from 'socket.io-client';
import { create } from 'zustand';

import { getDevice } from '~modules/device/api';
import { DeviceDto } from '~modules/device/model/dto.ts';
import { config } from '~shared/config';
import { randomUuid } from '~shared/helpers';
import { loadFromStorage, saveInStorage } from '~shared/store';

interface DeviceQRStoreState {
	socket: Socket | null;
	isConnected: boolean;
	qr: string | null;
	deviceCode: string;
	isManuallyDisconnected: boolean;
	connect: (deviceId: string, force?: boolean) => void;
	disconnect: () => void;
	time: Date;
	tick: () => void;
	device: DeviceDto | null;
}

export const useDeviceQRStore = create<DeviceQRStoreState>((set, get) => ({
	socket: null,
	isConnected: false,
	qr: null,
	time: new Date(),
	isManuallyDisconnected: false,
	device: null,
	deviceCode: loadFromStorage<string>('deviceCode') ?? randomUuid(),

	connect: (deviceNumber, force) => {
		const { deviceCode } = get();

		saveInStorage('deviceCode', deviceCode);
		if ((!force && get().isManuallyDisconnected) || !deviceNumber) return;

		getDevice(deviceNumber)
			.then(device => {
				set(old => ({ ...old, device }));
			})
			.catch(() => {
				// Устройство с таким номером не зарегистрировано - QR продолжаем показывать
				console.error(`Устройство ${deviceNumber} не найдено`);
				set(old => ({ ...old, device: null }));
			});

		const newSocket = io(config.wsUrl, {
			query: { deviceNumber: deviceNumber, deviceId: deviceCode },
			reconnection: true,
			reconnectionDelay: 2000,
			reconnectionDelayMax: 2000,
		});

		newSocket.on('connect', () => {
			console.log('✅ Connected');
			set({ isConnected: true, isManuallyDisconnected: false });
		});

		newSocket.on('disconnect', () => {
			console.warn('⚠️ Disconnected');
			set({ isConnected: false });
			if (!get().isManuallyDisconnected) {
				console.log('🔄 Attempting reconnection...');
			}
		});

		newSocket.on('connect_error', err => {
			console.error('❌ Connection error:', err);
		});

		newSocket.on('qr', msg => {
			console.log('qr', msg);
			set(state => ({ ...state, qr: msg }));
		});

		set({ socket: newSocket });
	},

	disconnect: () => {
		const { socket } = get();
		if (socket) {
			set({ isManuallyDisconnected: true, isConnected: false });
			socket.disconnect();
		}
	},

	tick: () => {
		set(old => ({ ...old, time: new Date() }));
	},
}));
